import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  FormControl,
  CircularProgress,
  Grid,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Box,
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { Company } from 'src/types/data';
import { formatRib } from 'src/utils/format/format_rib';
import { useLocales } from 'src/locales';
import { useSelector, useDispatch } from 'react-redux';
import { selectFilteredData } from 'src/redux/selectors';
import { addCarnetToRib } from 'src/redux/slices/dataSlice';

interface CarnetDialogProps {
  open: boolean;
  onClose: () => void;
  company?: number;
  ribId?: number;
  ribRib?: string;
}
interface SmallCompany {
  id: number,
  label: string
}

const CarnetDialog: React.FC<CarnetDialogProps> = ({ open, onClose, company, ribId, ribRib }) => {
  const filteredData = useSelector(selectFilteredData);
  const [selectedRib, setSelectedRib] = useState<number | null>(ribId || null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(company || null);
  const [companyOptions, setCompanyOptions] = useState<SmallCompany[]>([]);
  const [ribOptions, setRibOptions] = useState<SmallCompany[]>([]);
  const [firstCode, setFirstCode] = useState<string>('');
  const [size, setSize] = useState<string>('10');
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { makeRequest } = useServerRequest();
  const { t } = useLocales();
  const roles = [
    { id: '1', label: t('checks.check') },
    { id: '2', label: t('checks.effet') },
  ];
  const [type, setType] = useState<string>(roles[0].id);
  const dispatch = useDispatch();

  const handleCompanyChange = (event: any, newValue: { id: number; label: string } | null) => {
    setSelectedCompany(newValue?.id || null);

    if (newValue) {
      const tempSelectedCompany = filteredData.find(comp => comp.id === newValue.id);

      if (tempSelectedCompany) {
        const tempRibs = tempSelectedCompany.ribs.map(rb => ({
          id: rb.id,
          label: `${formatRib(rb.rib)} - ${rb.bref}`,
        }));

        setRibOptions(tempRibs);
      } else {
        setRibOptions([]);
      }
    } else {
      setRibOptions([]);
    }
  };

  const handleRibChange = (event: any, newValue: { id: number; label: string } | null) => {
    setSelectedRib(newValue?.id || null);
  };

  useEffect(() => {
    if (filteredData) {
      const tempCompanyOptions: SmallCompany[] = filteredData
        .filter((comp: Company) => comp.user_role !== 'analysis')
        .map((comp: Company) => ({
          id: comp.id,
          label: comp.name,
        }));
      setCompanyOptions(tempCompanyOptions);
    }
  }, [filteredData]);

  const validateFields = () => {
    const newErrors: { [key: string]: string } = {};

    const codePattern = /^[a-zA-Z]*\d+$/;
    if (!codePattern.test(firstCode)) {
      newErrors.firstCode = t('common.input.firstCode.error1');
    }

    if (!type) {
      newErrors.type = 'Type is required.';
    }

    if (!['10', '25', '50', '100'].includes(size)) {
      newErrors.size = t('m.e.rib');
    }

    if (!selectedRib) {
      newErrors.selectedRib = t('m.e.ribRequired');
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }
    if (!selectedCompany || !selectedRib) return;

    const formData = {
      rib_id: selectedRib,
      type,
      code: firstCode,
      taille: size,
    };

    setLoading(true);

    const response = await makeRequest(`/saveCarnet`, {
      method: 'POST',
      data: formData,
    }, t('common.alert.carnet'));

    setLoading(false);

    if (response.success && response.data) {
      dispatch(addCarnetToRib({ companyId: selectedCompany, ribId: selectedRib, carnet: response.data.carnet }))
      onClose();
    } else if (response.error && response.error.includes('unique')) {
      setErrors({ firstCode: t('common.input.firstCode.error2') });
    }
  };

  const handleTypeChange = (event: React.MouseEvent<HTMLElement>, newType: string | null) => {
    if (newType !== null) {
      setType(newType);
    }
  };

  // if (!selectedCompany || !selectedRib) return null;

  return (
    <Dialog open={open} disableEscapeKeyDown onClose={(_, reason) => reason !== 'backdropClick' && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{ribRib ? t('m.t.carnet', { name: ribRib }) : t('m.t.carnet1')}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal" required>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {/* Label in one line with toggle buttons */}
            <Typography variant="body1" component="label" sx={{ mr: 2 }}>
              {t('common.input.type.select')}
            </Typography>
            <ToggleButtonGroup
              value={type}
              exclusive
              onChange={handleTypeChange}
              aria-label={t('common.input.type.label')}
              sx={{ flexGrow: 1 }}
            >
              {roles.map((roleObj) => (
                <ToggleButton
                  key={roleObj.id}
                  value={roleObj.id}
                  aria-label={roleObj.label}
                  sx={{ flex: 1 }}
                >
                  {roleObj.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </FormControl>

        {(!ribId && !company) && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                options={companyOptions}
                getOptionLabel={(option) => option.label || ''}
                value={selectedCompany ? companyOptions?.find(comp => comp.id === selectedCompany) : null}
                onChange={handleCompanyChange}
                renderInput={(params) => <TextField {...params} label={t('common.input.company.select')} />}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={ribOptions}
                getOptionLabel={(option) => option.label || ''}
                value={selectedRib !== null ? ribOptions.find(rb => rb.id === selectedRib) : null}
                onChange={handleRibChange}
                renderInput={(params) => <TextField {...params} label={t('common.input.rib.select')} />}
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              label={t('common.input.firstCode.label')}
              value={firstCode}
              onChange={(e) => setFirstCode(e.target.value.toUpperCase())}
              placeholder="ABC12345"
              error={!!errors.firstCode}
              helperText={errors.firstCode}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal" error={!!errors.size}>
              <Autocomplete
                fullWidth
                options={['10', '25', '50', '100']}
                value={size}
                onChange={(event, newValue) => setSize(newValue as string)}
                renderInput={(params) => <TextField {...params} label={t('common.input.size.label')} />}
              />
              {errors.size && <p style={{ color: 'red' }}>{errors.size}</p>}
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={loading}>
          {t('common.button.action.cancel')}
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedRib || !firstCode}
        >
          {loading ? <CircularProgress size={24} /> : t('common.button.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarnetDialog;
